// Reads the chosen sheet files into the import's body, refusing first what the
// server would refuse (WarmupImportIn and the importer's image limits).

export const MOST_CSV_CHARACTERS = 1_000_000;
export const MOST_IMAGES = 100;
export const MOST_IMAGE_BYTES = 2_000_000;

const CSV_FIELDS = { sets: "sets_csv", questions: "questions_csv", misconceptions: "misconceptions_csv" };

const count = (n) => n.toLocaleString("en-US");

const readWith = (file, read) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    read(reader, file);
  });

const readText = (file) => readWith(file, (reader) => reader.readAsText(file, "UTF-8"));

const DATA_URL_PREFIX = /^data:[^,]*,/;
const readBase64 = async (file) =>
  (await readWith(file, (reader) => reader.readAsDataURL(file))).replace(DATA_URL_PREFIX, "");

const csvProblem = (file, text) => {
  if (text.length === 0) return `${file.name} is empty`;
  if (text.length > MOST_CSV_CHARACTERS) {
    return `${file.name} has ${count(text.length)} characters; at most ${count(MOST_CSV_CHARACTERS)} are allowed`;
  }
  return null;
};

const imagesProblem = (images) => {
  if (images.length > MOST_IMAGES) {
    return `${count(images.length)} images chosen; at most ${count(MOST_IMAGES)} images are allowed`;
  }
  const tooBig = images.find((image) => image.size > MOST_IMAGE_BYTES);
  if (tooBig) return `${tooBig.name} is ${count(tooBig.size)} bytes; at most ${count(MOST_IMAGE_BYTES)} are allowed`;
  return null;
};

/**
 * Answers {problems} when any file would be refused, keyed by input
 * ("sets", "questions", "misconceptions", "images"), else {body} with the three
 * CSV texts and the images.
 */
export const readSheet = async (csvs, images) => {
  const problems = {};
  const body = {};
  await Promise.all(
    Object.entries(CSV_FIELDS).map(async ([name, field]) => {
      const text = await readText(csvs[name]);
      const problem = csvProblem(csvs[name], text);
      if (problem) problems[name] = problem;
      body[field] = text;
    })
  );
  const imageProblem = imagesProblem(images);
  if (imageProblem) problems.images = imageProblem;
  if (Object.keys(problems).length > 0) return { problems };

  body.images = await Promise.all(
    images.map(async (image) => ({ filename: image.name, content_base64: await readBase64(image) }))
  );
  return { body };
};
